/**
 * AdminSupportInbox — /admin/support
 *
 * Operator console for the support team. Lists all support conversations on
 * the left with unread badges, opens the selected thread on the right with a
 * reply composer + a status toggle (Open / Closed).
 *
 * Realtime: subscribes to /api/ws/chat and listens for { type: "support.new" }
 * pushes from any user. New conversations and incoming messages appear
 * without refresh. A 30s slow refresh runs as a safety net.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, useLanguage, API } from "@/App";
import { DashboardLayout } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Headphones, Send, ChevronLeft, ChevronRight, CheckCircle2, RotateCcw, Loader2, Inbox } from "lucide-react";

export default function AdminSupportInbox() {
  const { token, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === "ar";
  const isAdmin = ["admin", "owner", "support"].includes(user?.role);

  const [conversations, setConversations] = useState([]);
  const [filter, setFilter] = useState("open"); // open|closed|all
  const [active, setActive] = useState(null);   // conv_id
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    if (!isAdmin) { navigate("/dashboard"); return; }
  }, [token, isAdmin, navigate]);

  const loadList = async () => {
    try {
      const r = await axios.get(`${API}/admin/support/conversations`, {
        params: filter === "all" ? {} : { status: filter },
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadConversation = async (conv_id) => {
    try {
      const r = await axios.get(`${API}/admin/support/conversations/${conv_id}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(r.data.messages || []);
      // Refresh list to reflect zeroed unread
      loadList();
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (token && isAdmin) loadList(); /* eslint-disable-next-line */ }, [filter, token, isAdmin]);
  useEffect(() => { if (active) loadConversation(active); /* eslint-disable-next-line */ }, [active]);

  // WebSocket realtime — listen for any support.new and refresh as needed.
  useEffect(() => {
    if (!token || !isAdmin) return;
    let ws = null, reconnect = null;
    const lifecycle = { cancelled: false };
    const wsBase = API.replace(/\/api$/, "").replace(/^http/, "ws");
    const connect = () => {
      if (lifecycle.cancelled) return;
      try { ws = new WebSocket(`${wsBase}/api/ws/chat?token=${encodeURIComponent(token)}`); }
      catch { return; }
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data?.type === "support.new") {
            // Always refresh the list (unread counters / last message preview)
            loadList();
            // If the user is currently viewing this conversation, append the new msg.
            if (active && data.conv_id === active) {
              setMessages((prev) => prev.some((m) => m.message_id === data.message.message_id) ? prev : [...prev, data.message]);
            }
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (!lifecycle.cancelled) reconnect = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws && ws.close(); } catch { /* noop */ } };
    };
    connect();
    const iv = setInterval(loadList, 30000);
    return () => {
      lifecycle.cancelled = true;
      clearInterval(iv);
      if (reconnect) clearTimeout(reconnect);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin, active]);

  // Scroll bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, active]);

  const send = async () => {
    const text = draft.trim();
    if (!text || !active) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/support/conversations/${active}/messages`,
        { content: text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessages((prev) => [...prev, r.data.message]);
      setDraft("");
      loadList();
    } catch {
      toast.error(t("تعذّر الإرسال", "Send failed"));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    if (!active) return;
    try {
      await axios.post(
        `${API}/admin/support/conversations/${active}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(status === "closed" ? t("تم إغلاق المحادثة", "Conversation closed") : t("تم إعادة فتح المحادثة", "Conversation re-opened"));
      loadList();
      loadConversation(active);
    } catch {
      toast.error(t("تعذّر تحديث الحالة", "Status update failed"));
    }
  };

  const activeConv = conversations.find((c) => c.conv_id === active);
  const Chev = isAr ? ChevronLeft : ChevronRight;

  return (
    <DashboardLayout title={t("صندوق الدعم الفني", "Support Inbox")}>
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-3" data-testid="admin-support-inbox">

        {/* ── Sidebar: conversation list ── */}
        <div className="glass-card rounded-2xl overflow-hidden flex flex-col h-[75vh]">
          <div className="p-2 border-b border-slate-800 flex gap-1">
            {["open", "closed", "all"].map((k) => (
              <Button
                key={k}
                size="sm"
                variant={filter === k ? "default" : "outline"}
                onClick={() => setFilter(k)}
                className={filter === k ? "btn-primary" : "border-slate-700 text-slate-300"}
                data-testid={`support-filter-${k}`}
              >
                {k === "open"   ? t("مفتوحة", "Open")
                 : k === "closed"? t("مغلقة", "Closed") : t("الكل", "All")}
              </Button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center"><Loader2 className="animate-spin text-yellow-500 mx-auto" /></div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                <Inbox className="mx-auto mb-2 opacity-40" />{t("لا توجد محادثات", "No conversations")}
              </div>
            ) : conversations.map((c) => (
              <button
                key={c.conv_id}
                onClick={() => setActive(c.conv_id)}
                className={`w-full text-start px-3 py-2.5 border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors ${active === c.conv_id ? "bg-slate-800/60" : ""}`}
                data-testid={`support-conv-${c.conv_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white text-sm font-semibold truncate">{c.user_name || c.user_email}</span>
                  {c.unread_for_admin > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`unread-${c.conv_id}`}>
                      {c.unread_for_admin}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-400 truncate mt-0.5">{c.last_message || t("(فارغة)", "(empty)")}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-between">
                  <span>{new Date(c.last_message_at).toLocaleString(isAr ? "ar-EG" : undefined, { dateStyle: "short", timeStyle: "short" })}</span>
                  <Badge className={c.status === "open" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[9px]" : "bg-slate-600/30 text-slate-300 border-slate-500/40 text-[9px]"}>
                    {c.status === "open" ? t("مفتوحة", "Open") : t("مغلقة", "Closed")}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Thread: messages + composer ── */}
        <div className="glass-card rounded-2xl overflow-hidden flex flex-col h-[75vh]">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm">
              <Headphones size={42} className="opacity-30 mb-2" />
              {t("اختر محادثة من القائمة للرد عليها.", "Pick a conversation from the list to start replying.")}
            </div>
          ) : (
            <>
              <div className="p-2.5 border-b border-slate-800 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <button onClick={() => setActive(null)} className="md:hidden text-slate-400 hover:text-white" aria-label="back">
                    <Chev size={18} />
                  </button>
                  <div className="min-w-0">
                    <div className="text-white text-sm font-semibold truncate">{activeConv?.user_name || activeConv?.user_email}</div>
                    <div className="text-[11px] text-slate-400 truncate">{activeConv?.user_email}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {activeConv?.status === "open" ? (
                    <Button size="sm" variant="outline" onClick={() => setStatus("closed")} className="border-slate-700 text-slate-300" data-testid="support-close-btn">
                      <CheckCircle2 size={14} className="ml-1" />{t("إغلاق", "Close")}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setStatus("open")} className="border-emerald-500/40 text-emerald-300" data-testid="support-reopen-btn">
                      <RotateCcw size={14} className="ml-1" />{t("إعادة فتح", "Re-open")}
                    </Button>
                  )}
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="support-thread">
                {messages.length === 0 ? (
                  <div className="text-center text-slate-500 text-sm py-10">{t("لا توجد رسائل هنا بعد", "No messages here yet")}</div>
                ) : messages.map((m) => {
                  const fromAdmin = m.sender_role === "admin";
                  return (
                    <div key={m.message_id} className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                        fromAdmin
                          ? "bg-yellow-500/90 text-black rounded-br-sm"
                          : "bg-slate-800/80 text-slate-100 rounded-bl-sm border border-slate-700"
                      }`}>
                        {m.attachment_url && m.attachment_type === "image" && (
                          <a href={m.attachment_url} target="_blank" rel="noopener noreferrer">
                            <img src={m.attachment_url} alt="" className="rounded-lg max-h-56 mb-1" />
                          </a>
                        )}
                        {m.content && <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>}
                        <div className="text-[10px] opacity-70 mt-0.5 text-end">
                          {new Date(m.created_at).toLocaleString(isAr ? "ar-EG" : undefined, { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-2 border-t border-slate-800 flex gap-2">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={t("اكتب ردك للعميل…", "Type your reply…")}
                  className="flex-1 h-11 bg-slate-900/70 border-slate-700 text-white placeholder:text-slate-500"
                  disabled={sending}
                  data-testid="support-admin-input"
                />
                <Button onClick={send} disabled={sending || !draft.trim()} className="btn-primary h-11 w-11 p-0" data-testid="support-admin-send">
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
